import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Layers,
} from 'lucide-react';
import { CONTENT_TEMPLATES, templateForTheme } from '@/data/contentTemplates';
import {
  listWeeks,
  listScheduledPosts,
  GeneratedWeek,
  ScheduledPost,
} from '@/lib/brandStudio';

/**
 * TEMPLATE BALANCE — insights card for the Content Planner.
 *
 * Analyzes every generated week (Brand Studio → generate-week-from-voice),
 * recovers each post's content template from its theme prefix (e.g.
 * "Food Spotlight — The Art of Slow Dining" → "Food Spotlight"), tallies how
 * often each of the 25 templates has been used, and surfaces the least-used
 * templates so the team keeps the feed varied instead of leaning on favorites.
 */

interface TemplateStat {
  id: number;
  name: string;
  count: number;
  share: number; // 0..100 of matched posts
}

const TemplateBalanceCard: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState<GeneratedWeek[]>([]);
  const [posts, setPosts] = useState<ScheduledPost[]>([]);

  const load = async () => {
    setLoading(true);
    const [w, p] = await Promise.all([listWeeks(), listScheduledPosts()]);
    setWeeks(w);
    // Only posts that belong to a generated week count toward template balance.
    const weekIds = new Set(w.map((x) => x.id));
    setPosts(p.filter((post) => post.week_id && weekIds.has(post.week_id)));
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const { stats, matched, unmatched, maxCount, leastUsed, usedCount } = useMemo(() => {
    const counts = new Map<number, number>();
    CONTENT_TEMPLATES.forEach((t) => counts.set(t.id, 0));
    let matchedTotal = 0;
    let unmatchedTotal = 0;

    posts.forEach((p) => {
      const tpl = templateForTheme(p.theme);
      if (tpl) {
        counts.set(tpl.id, (counts.get(tpl.id) || 0) + 1);
        matchedTotal += 1;
      } else {
        unmatchedTotal += 1;
      }
    });

    const all: TemplateStat[] = CONTENT_TEMPLATES.map((t) => ({
      id: t.id,
      name: t.name,
      count: counts.get(t.id) || 0,
      share: matchedTotal > 0 ? Math.round(((counts.get(t.id) || 0) / matchedTotal) * 100) : 0,
    }));

    const sortedDesc = [...all].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    const max = sortedDesc.length ? Math.max(1, sortedDesc[0].count) : 1;

    // Least-used: ascending by count; take the bottom 6 so the team knows
    // exactly which templates to reach for next.
    const least = [...all]
      .sort((a, b) => a.count - b.count || a.name.localeCompare(b.name))
      .slice(0, 6);

    return {
      stats: sortedDesc,
      matched: matchedTotal,
      unmatched: unmatchedTotal,
      maxCount: max,
      leastUsed: least,
      usedCount: all.filter((t) => t.count > 0).length,
    };
  }, [posts]);

  return (
    <div className="bg-white border border-slate-200 mb-6">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-100">
        <button onClick={() => setOpen(!open)} className="flex-shrink-0">
          {open ? (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-500" />
          )}
        </button>
        <div className="w-8 h-8 rounded bg-[#C9A961]/10 border border-[#C9A961]/30 flex items-center justify-center flex-shrink-0">
          <BarChart3 className="w-4 h-4 text-[#C9A961]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-base text-[#1A3B5C]">
            Template Balance{' '}
            <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#8B6F47] ml-2">
              Insights
            </span>
          </h3>
          <p className="font-sans text-[11px] text-slate-500 truncate">
            How evenly the 25 content templates are used across all AI-generated weeks
          </p>
        </div>

        {/* Quick stats */}
        {!loading && (
          <div className="hidden md:flex items-center gap-4 pr-2">
            <Stat label="Weeks" value={weeks.length} />
            <Stat label="Posts" value={matched + unmatched} />
            <Stat label="Templates Used" value={`${usedCount}/${CONTENT_TEMPLATES.length}`} />
          </div>
        )}

        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 text-slate-600 font-sans text-[10px] tracking-[0.2em] uppercase hover:border-[#C9A961] hover:text-[#C9A961] transition-colors"
          title="Re-analyze all generated weeks"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {open && (
        <div className="p-5">
          {loading ? (
            <div className="py-10 text-center font-sans text-sm text-slate-400">
              Analyzing generated weeks...
            </div>
          ) : matched + unmatched === 0 ? (
            <div className="py-10 text-center">
              <Layers className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="font-sans text-sm text-slate-500">
                No AI-generated weeks yet.
              </p>
              <p className="font-sans text-xs text-slate-400 mt-1">
                Generate a week in the Brand Studio and its posts will appear here, tallied by
                content template.
              </p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-[1fr_320px] gap-8">
              {/* ---- Bar chart: usage per template ---- */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-sans text-[11px] tracking-[0.25em] uppercase text-[#1A3B5C] font-semibold">
                    Usage by Template
                  </h4>
                  <span className="font-sans text-[10px] text-slate-400">
                    {matched} matched post{matched === 1 ? '' : 's'} across {weeks.length} week
                    {weeks.length === 1 ? '' : 's'}
                  </span>
                </div>

                <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                  {stats.map((t) => (
                    <div key={t.id} className="flex items-center gap-3">
                      <span
                        className="font-sans text-[11px] text-slate-600 w-[46%] truncate"
                        title={t.name}
                      >
                        {t.name}
                      </span>
                      <div className="flex-1 h-3 bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${
                            t.count === 0
                              ? ''
                              : 'bg-gradient-to-r from-[#C9A961] to-[#8B6F47]'
                          }`}
                          style={{ width: `${(t.count / maxCount) * 100}%` }}
                        />
                      </div>
                      <span
                        className={`font-sans text-[11px] tabular-nums w-8 text-right ${
                          t.count === 0 ? 'text-slate-300' : 'text-[#1A3B5C] font-semibold'
                        }`}
                      >
                        {t.count}
                      </span>
                    </div>
                  ))}
                </div>

                {unmatched > 0 && (
                  <p className="mt-4 font-sans text-[11px] text-slate-400 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-[#C9A961]" />
                    {unmatched} legacy post{unmatched === 1 ? '' : 's'} without a template prefix
                    were excluded from the tally.
                  </p>
                )}
              </div>

              {/* ---- Least-used templates ---- */}
              <div className="lg:border-l lg:border-slate-100 lg:pl-8">
                <h4 className="font-sans text-[11px] tracking-[0.25em] uppercase text-[#1A3B5C] font-semibold mb-1">
                  Reach for These Next
                </h4>
                <p className="font-sans text-[11px] text-slate-500 mb-4">
                  The least-used templates — feature them in upcoming weeks to keep the feed
                  varied.
                </p>
                <div className="space-y-2">
                  {leastUsed.map((t, idx) => (
                    <div
                      key={t.id}
                      className="flex items-center gap-3 bg-slate-50 border border-slate-200 px-3 py-2.5"
                    >
                      <span className="font-serif text-sm text-[#C9A961] w-5 text-center flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-xs text-[#0D1B2A] font-medium truncate">
                          {t.name}
                        </p>
                        <p className="font-sans text-[10px] text-slate-400">
                          {t.count === 0
                            ? 'Never used'
                            : `Used ${t.count} time${t.count === 1 ? '' : 's'} · ${t.share}% of posts`}
                        </p>
                      </div>
                      {t.count === 0 ? (
                        <span className="font-sans text-[9px] tracking-wider uppercase px-2 py-1 bg-rose-50 text-rose-600 border border-rose-200 flex-shrink-0">
                          Unused
                        </span>
                      ) : (
                        <span className="font-sans text-[9px] tracking-wider uppercase px-2 py-1 bg-[#C9A961]/10 text-[#8B6F47] border border-[#C9A961]/30 flex-shrink-0">
                          Low
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {usedCount === CONTENT_TEMPLATES.length && (
                  <p className="mt-4 font-sans text-[11px] text-emerald-600 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Every template has been used at least once — great variety!
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="text-right">
    <div className="font-serif text-base text-[#1A3B5C] leading-none">{value}</div>
    <div className="font-sans text-[9px] tracking-wider uppercase text-slate-400 mt-0.5">
      {label}
    </div>
  </div>
);

export default TemplateBalanceCard;
